import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BasePageLayout,
  BaseBox,
  BaseTypography,
  BaseButton,
} from "../../Common/UI/exports";
import { deployRepositories } from "../../APIs/multiDeploymentAPI";
import { fetchRepositories } from "../../APIs/AzureAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Simple in-memory cache
const cache = {
  repositories: null,
};

function MultiDeployment() {
  const email = localStorage.getItem("email");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [repositories, setRepositories] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFilterChange = (e) => setFilterText(e.target.value);

  const filteredRepositories = repositories.filter((repo) =>
    repo.name.toLowerCase().includes(filterText.toLowerCase())
  );

  const selectedCount = repositories.filter((repo) => repo.selected).length;

  const toggleSelectAll = () => {
    const allSelected = filteredRepositories.every((repo) => repo.selected);
    setRepositories((prevRepos) =>
      prevRepos.map((repo) =>
        filteredRepositories.includes(repo)
          ? { ...repo, selected: !allSelected }
          : repo
      )
    );
  };

  useEffect(() => {
    const loadRepositories = async () => {
      if (cache.repositories) {
        setRepositories(cache.repositories);
      } else {
        try {
          const data = await fetchRepositories();
          setRepositories(data);
          cache.repositories = data;
        } catch (error) {
          console.error("Error loading repositories:", error);
          toast.error("Failed to load repositories.");
        }
      }
    };
    loadRepositories();
  }, []);

  const handleBack = () => navigate(-1);

  const handleDeploy = async () => {
    setLoading(true);
    const selectedRepos = repositories
      .filter((repo) => repo.selected)
      .map((repo) => ({
        repositoryName: repo.name,
        sourceBranch: repo.branch || "",
      }));

    try {
      await deployRepositories(selectedRepos, token);
      toast.success("Deployment initiated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Deployment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeployCheckboxChange = (index) => {
    const repoId = filteredRepositories[index].id;
    setRepositories((prevRepos) =>
      prevRepos.map((repo) =>
        repo.id === repoId ? { ...repo, selected: !repo.selected } : repo
      )
    );
  };

  const handleBranchChange = (index, value) => {
    const repoId = filteredRepositories[index].id;
    setRepositories((prevRepos) =>
      prevRepos.map((repo) =>
        repo.id === repoId ? { ...repo, branch: value } : repo
      )
    );
  };

  return (
    <BasePageLayout sidebarItems={[]}>
      <BaseBox
        sx={{
          height: "calc(100vh - 140px)", 
          display: "flex",
          flexDirection: "column",
          maxWidth: "1160px",
          mx: "auto",
          mt: 0,
          px: 2,
        }}
      >
        <BaseTypography sx={{ fontSize: "1.2rem", mb: 2, fontWeight: 600 }}>
          Multi Deployment
        </BaseTypography>

        {/* Filter & Select All */}
<BaseBox
  sx={{
    display: "flex",
    flexDirection: "column",
    gap: 2,
    mb: 3,
  }}
>
  {/* Row 1: Filter input */}
  <BaseBox sx={{ display: "flex", justifyContent: "flex-start" }}>
    <input
      type="text"
      placeholder="Filter repositories..."
      value={filterText}
      onChange={handleFilterChange}
      style={{
        padding: "6px 8px",
        borderRadius: "4px",
        border: "1px solid #ccc",
        minWidth: "250px",
      }}
    />
  </BaseBox>

  {/* Row 2: Select button + count */}
  <BaseBox
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      flexWrap: "wrap",
      justifyContent: "flex-start",
    }}
  >
    <BaseButton
      onClick={toggleSelectAll}
      color="primary"
      variant="outlined"
      sx={{
        width: "auto",
        minWidth: "fit-content",
        px: 2,
      }}
    >
      {filteredRepositories.every((repo) => repo.selected)
        ? "Deselect All"
        : "Select All"}
    </BaseButton>

    <BaseTypography sx={{ fontSize: "0.9rem" }}>
      {selectedCount} selected out of {repositories.length}
    </BaseTypography>
  </BaseBox>
</BaseBox>



        {/* Repositories Table */}
        <BaseBox
          sx={{
            flexGrow: 1, // fill available height
            overflowY: "auto",
            border: "1px solid #ccc",
          }}
        >
          <table className="repositories-table" style={{ width: "100%" }}>
            <thead style={{ backgroundColor: "#c4001d", color: "#fff" }}>
              <tr>
                <th style={{ padding: "8px", color: "#040404ff" }}>Name</th>
                <th style={{ padding: "8px", color: "#040404ff" }}>Deploy</th>
                <th style={{ padding: "8px", color: "#040404ff" }}>Branch</th>
              </tr>
            </thead>
            <tbody>
              {filteredRepositories.length ? (
                filteredRepositories.map((repo, index) => (
                  <tr key={repo.id}>
                    <td style={{ padding: "6px 8px" }}>{repo.name}</td>
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={repo.selected || false}
                        onChange={() => handleDeployCheckboxChange(index)}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="Branch name"
                        value={repo.branch || ""}
                        onChange={(e) =>
                          handleBranchChange(index, e.target.value)
                        }
                        style={{
                          padding: "4px 6px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          width: "100%",
                        }}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    style={{ textAlign: "center", padding: "8px" }}
                  >
                    No repositories match your filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </BaseBox>

        {/* Buttons */}
        <BaseBox sx={{ display: "flex", gap: 2, mt: 2 }}>
          <BaseButton onClick={handleBack} variant="outlined">
            Back
          </BaseButton>

          <BaseButton onClick={handleDeploy} color="primary" disabled={loading}>
            {loading ? "Deploying" : "Deploy"}
          </BaseButton>
        </BaseBox>
      </BaseBox>

      {/* Toast Notifications */}
      <ToastContainer />
    </BasePageLayout>
  );
}

export default MultiDeployment;
